/**
 * 🌤️ WEATHER ICON COMPONENT
 * Shared utility for displaying weather condition icons and temperature
 * 
 * Mission 14: Weather & Climate Intelligence - Phase 3 Frontend
 * Part of Coach Cedar's weather intelligence system
 */

import React from 'react';

export interface WeatherIconProps {
  condition: string;
  temperature?: number;
  size?: 'sm' | 'md' | 'lg';
  showTemp?: boolean;
  className?: string;
}

/**
 * Get emoji icon for weather condition
 * 
 * Supports OpenWeatherMap condition types:
 * - clear, clouds, rain, drizzle, snow, thunderstorm, fog, mist
 */
const getWeatherEmoji = (condition: string): string => {
  const emojiMap: Record<string, string> = {
    'clear': '☀️',
    'clouds': '☁️',
    'rain': '🌧️',
    'drizzle': '🌦️',
    'snow': '🌨️',
    'thunderstorm': '⛈️',
    'fog': '🌫️',
    'mist': '🌫️',
    'haze': '🌫️'
  };
  
  return emojiMap[condition.toLowerCase()] || '🌤️';
};

/**
 * WeatherIcon Component
 * 
 * Displays weather condition icon with optional temperature
 * 
 * @example
 * ```tsx
 * <WeatherIcon condition="clear" temperature={15} showTemp={true} size="md" />
 * // Renders: ☀️ 15°C
 * ```
 */
export const WeatherIcon: React.FC<WeatherIconProps> = ({
  condition,
  temperature,
  size = 'md',
  showTemp = false,
  className = ''
}) => {
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };
  
  const emoji = getWeatherEmoji(condition);
  
  return (
    <div className={`inline-flex items-center gap-1 ${sizeClasses[size]} ${className}`}>
      <span className="select-none" role="img" aria-label={`${condition} weather`}>
        {emoji}
      </span>
      {showTemp && temperature !== undefined && (
        <span className="font-medium text-gray-700">
          {Math.round(temperature)}°C
        </span>
      )}
    </div>
  );
};

/**
 * WeatherBand Component
 * 
 * Compact horizontal weather display with condition and temperature range
 * Used on workout cards and "Today" section
 * 
 * @example
 * ```tsx
 * <WeatherBand 
 *   condition="clear" 
 *   tempHigh={18} 
 *   tempLow={12} 
 *   size="sm"
 * />
 * // Renders: ☀️ 12-18°C
 * ```
 */
export interface WeatherBandProps {
  condition: string;
  tempHigh?: number;
  tempLow?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const WeatherBand: React.FC<WeatherBandProps> = ({
  condition,
  tempHigh,
  tempLow,
  size = 'sm',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };
  
  const emoji = getWeatherEmoji(condition);
  
  // 🎯 JAN 19, 2026: Format temperature display with "/" separator
  // Problem: "-15--11°C" looks odd (minus fifteen minus minus eleven)
  // Solution: Use "/" separator → "-15/-11°C"
  const tempDisplay = tempHigh !== undefined && tempLow !== undefined
    ? `${Math.round(tempLow)}/${Math.round(tempHigh)}°C`
    : tempHigh !== undefined
    ? `${Math.round(tempHigh)}°C`
    : '';
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1.5
        bg-gray-50 rounded-md
        ${sizeClasses[size]}
        ${className}
      `}
      role="status"
      aria-label={`Weather: ${condition}, ${tempDisplay}`}
    >
      <span className="select-none text-base" role="img" aria-label={`${condition} weather`}>
        {emoji}
      </span>
      {tempDisplay && (
        <span className="font-medium text-gray-700">
          {tempDisplay}
        </span>
      )}
    </div>
  );
};

/**
 * WeatherConditionText Component
 * 
 * Human-readable weather condition text
 * Capitalizes and formats condition names
 */
export const WeatherConditionText: React.FC<{ condition: string; className?: string }> = ({
  condition,
  className = ''
}) => {
  const formatted = condition
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return (
    <span className={`text-gray-600 ${className}`}>
      {formatted}
    </span>
  );
};

export default WeatherIcon;
